/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import axios from 'axios';

const versionNumber = '999';

const Platform = 'web';

const isUsApi = window.location.hostname !== process.env.REACT_APP_FE_BASE_HOSTNAME;

const baseURL = isUsApi
  ? process.env.REACT_APP_US_API_BASE_URL
  : process.env.REACT_APP_API_BASE_URL;

const BAANX_API = 'api';
const CARD_DETAILS_PAGE_TIMEOUT_MS = 15000;
const IFRAME_API = 'iframe/api';

const isEmbeddedInLedgerLiveApp = !!window.ElectronWebview;

const customOrigin =
  (window?.location?.ancestorOrigins?.length > 0 &&
    `Embedded ${window?.location?.ancestorOrigins?.item(
      window?.location?.ancestorOrigins?.length - 1
    )}`) ||
  (document?.referrer &&
    new URL(document?.referrer)?.origin !== new URL(window?.location?.href)?.origin &&
    `Embedded ${new URL(document?.referrer)?.origin}`) ||
  `Standalone ${window.origin}`;

const customHeaderOrigin = isEmbeddedInLedgerLiveApp ? `Embedded Ledger Live App` : customOrigin;

let axiosConnection = null;

export function getAxiosConnection() {
  if (!axiosConnection) {
    axiosConnection = axios.create({
      baseURL,
      headers: {
        'X-Request-Origin': customHeaderOrigin,
        'X-Product-Version': `${versionNumber}`,
        'X-Platform': Platform,
        'Content-Type': 'application/json',
        'X-product': `${
          process.env.REACT_APP_FLAVOR ? process.env.REACT_APP_FLAVOR.toUpperCase() : 'LEDGER'
        }`,
      },
    });

    axiosConnection.interceptors.response.use((response) => {
      const { data } = response;
      if (data.error) {
        throw data.error;
      }
      return response;
    });
  }
  return axiosConnection;
}

export const market = {
  allRates: async () => {
    const { data } = await axiosConnection.get(`${BAANX_API}/v1/marketrates`);
    const { rates } = data;
    return rates;
  },
};

export const settings = {
  all: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/settings`);
    return data;
  },
  newAll: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/new_settings`);
    if (data && data?.settings) {
      // eslint-disable-next-line no-shadow
      const { settings } = data;
      return settings;
    }
    return data;
  },
  byKeys: async (keys) => {
    const stringKeysWithComma = keys?.toString() || keys;

    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/new_settings?keys=${stringKeysWithComma}`
    );

    if (data && data?.settings) {
      // eslint-disable-next-line no-shadow
      const { settings } = data;
      return settings;
    }

    return data;
  },
  userRegionSettings: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/user/regional-settings`);
    return data;
  },
  supportedCountries: async () => {
    const { data } = await axiosConnection.get(`${BAANX_API}/v1/settings/supported_countries`);
    return data;
  },
  tierStandards: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/tiers/risk/standard/all`);
    return data;
  },
  supportedCurrencies: async () => {
    const { data } = await axiosConnection.get(`${BAANX_API}/v1/settings/supported_currencies`);
    return data;
  },
  usStates: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/us_states`);
    return data;
  },
  countrySubdivisions: async (countryCode) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/countries/${countryCode}/subdivisions`
    );
    return data;
  },
};

export const publicKey = {
  password: async () => {
    const { data } = await axiosConnection.get(`kms/public_key`);
    return data;
  },
};

export const user = {
  login: async ({ username, password, otpauth, token: recaptchaToken }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/auth/login`, {
      username,
      password,
      otpauth,
      recaptchaToken,
    });

    return data;
  },
  logout: async () => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/auth/logout`, {
      expire_all_sessions: false,
    });
    return data;
  },
  liveAddresses: async ({ live_addresses }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user_ledger_live_addresses`, {
      live_addresses,
    });

    return data;
  },
  tiersRishAssessments: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/user/tiers/risk/assessments`);
    return data;
  },
  partialSignup: async (userData) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user_onboarding`, userData);
    return data;
  },
  totpSecret: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/totp/secret`);
    return data;
  },
  totpVerify: async ({ token, email }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/totp/verify`, { token, email });
    return data;
  },
  signup: async ({
    password,
    first_name,
    middle_name,
    last_name,
    email,
    country_code,
    phone_number,
    title = 'Other',
    gender = 'Other',
    dateOfBirth,
    addressLine1,
    addressLine2,
    cityOrTown,
    postcode,
    countryName,
    selected_country_alpha,
    promoCode,
    selected_country_of_nationality_iso3166alpha3,
    selected_country_of_income_earned_iso3166alpha3,
    countryOfResidenceId,
    us_state,
    state_region,
    mailingAddress,
    ssn,
    verificationId,
    purposeOfAccount,
    token: recaptchaToken,
    allowMarketing,
    smsOptIn,
    place_of_birth_iso3166alpha3,
    tin,
    tin_country_iso3166alpha3,
    occupation,
    occupationOther,
    sourceOfIncome,
    sourceOfIncomeOther,
    businessSector,
    estimatedIncome,
    second_selected_country_of_nationality_iso3166alpha3,
    third_selected_country_of_nationality_iso3166alpha3,
    second_selected_country_of_income_earned_iso3166alpha3,
    third_selected_country_of_income_earned_iso3166alpha3,
    portugalInvestmentScheme,
  }) => {
    const bodyRequest = {
      username: email,
      password,
      first_name,
      middle_name,
      last_name,
      email,
      country_code,
      phone_number,
      title,
      gender,
      dateOfBirth,
      addressLine1,
      addressLine2,
      cityOrTown,
      postcode,
      countryName,
      selected_country: selected_country_alpha,
      promoCode,
      country_of_birth: place_of_birth_iso3166alpha3,
      selected_country_of_nationality: selected_country_of_nationality_iso3166alpha3,
      selected_country_where_income_earned: selected_country_of_income_earned_iso3166alpha3,
      country_of_residence: countryOfResidenceId,
      mailingAddress,
      us_state,
      state_region,
      ssn,
      purposeOfAccount,
      recaptchaToken,
      allow_marketing: allowMarketing,
      verification_id: verificationId,
      tin,
      tin_country: tin_country_iso3166alpha3,
      occupation,
      source_of_wealth: sourceOfIncome,
      business_sector: businessSector,
      estimated_annual_income: estimatedIncome,
    };

    if (smsOptIn) {
      bodyRequest.is_sms_opt_in = smsOptIn;
    }

    if (second_selected_country_of_nationality_iso3166alpha3) {
      bodyRequest.second_selected_country_of_nationality =
        second_selected_country_of_nationality_iso3166alpha3;
    }
    if (third_selected_country_of_nationality_iso3166alpha3) {
      bodyRequest.third_selected_country_of_nationality =
        third_selected_country_of_nationality_iso3166alpha3;
    }

    if (second_selected_country_of_income_earned_iso3166alpha3) {
      bodyRequest.second_selected_country_where_income_earned =
        second_selected_country_of_income_earned_iso3166alpha3;
    }
    if (third_selected_country_of_income_earned_iso3166alpha3) {
      bodyRequest.third_selected_country_where_income_earned =
        third_selected_country_of_income_earned_iso3166alpha3;
    }

    if (portugalInvestmentScheme) {
      bodyRequest.portugal_investment_scheme = portugalInvestmentScheme;
    }

    if (occupation === 'other_specify_occupation' && occupationOther) {
      bodyRequest.occupation_free_text = occupationOther;
    }

    if (sourceOfIncome === 'other_specify_source_of_wealth' && sourceOfIncomeOther) {
      bodyRequest.source_of_wealth_free_text = sourceOfIncomeOther;
    }

    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/users/create_new`, bodyRequest);
    return data;
  },
  validateWaitingListEmail: async ({ email }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/account/waitlist/email/check_status`,
      {
        email,
      }
    );

    return data;
  },
  verifyEmail: async ({ email, token: recaptchaToken, country_id }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/account/email/verification/send`, {
      email,
      recaptchaToken,
      country_id,
    });

    return data;
  },
  verifyEmailByCode: async ({ email, code, codeScope, verificationId, token: recaptchaToken }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/account/verification_codes/verify`,
      {
        email,
        code,
        verification_id: verificationId,
        code_scope: codeScope,
        recaptchaToken,
      }
    );

    return data;
  },
  wallets: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/user/wallets`);
    return data;
  },
  addHardWalletIdToWallet: async ({ wallet_address, hard_wallet_id }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/user/addresses/update/hard_wallet`,
      {
        wallet_address,
        hard_wallet_id,
      }
    );

    return data;
  },
  // TODO: REMOVE THIS ONE NOT USED ANYWHERE
  isEmailVerified: async (token) => {
    const { data } = await axiosConnection.get(
      `${BAANX_API}/v1/account/verification/is_email_verified?token=${encodeURIComponent(token)}`
    );

    return data;
  },
  phoneVerify: async ({
    country_code,
    phone_number,
    via,
    verificationId,
    token: recaptchaToken,
    isSignUp = false,
  }) => {
    const { data } = await axiosConnection.post(
      encodeURI(`${IFRAME_API}/v2/account/phone_verify/start`),
      {
        country_code,
        phone_number,
        via,
        verification_id: verificationId,
        recaptchaToken,
        is_sign_up: isSignUp,
      }
    );
    return data;
  },
  phoneCheck: async ({
    country_code,
    phone_number,
    verification_code,
    verification_id,
    token: recaptchaToken,
    attachSecret = false,
    email,
  }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/account/phone_verify/check`, {
      country_code,
      phone_number,
      verification_code,
      verification_id,
      recaptchaToken,
      attachSecret,
      email,
    });
    return data;
  },
  phoneVerifyTransaction: async ({ authy_id, user_id, mode, token: recaptchaToken }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/request2faAuthyCode`, {
      authy_id,
      mode,
      user_id,
      recaptchaToken,
    });
    return data;
  },
  phoneVerifyAuthyId: async ({ authy_id, user_id, mode, token: recaptchaToken }) => {
    if (user_id) {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/request2faCodeWithUser`, {
        authy_id,
        mode,
        user_id,
        recaptchaToken,
      });
      return data;
    }
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/request2faCode`, {
      authy_id,
      mode,
      recaptchaToken,
    });
    return data;
  },
  resetOTP: async ({ authy_id, user_id, email, token: recaptchaToken }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/totp/reset-otp`, {
      user_id,
      authy_id,
      email,
      recaptchaToken,
    });
    return data;
  },
  verifyResetOtp: async ({ email, otpauth, token: recaptchaToken }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/totp/verify-otp`, {
      email,
      otpauth,
      recaptchaToken,
    });
    return data;
  },
  resetPasswordCheckEmail: async ({ email }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/totp/check/email`, { email });
    return data;
  },
  verifyTOTPByEmail: async ({ email, token }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/totp/verify-token-by-email`, {
      email,
      token,
    });
    return data;
  },
  details: async () => {
    const { data } = await axiosConnection.get(`${BAANX_API}/v1/auth/authenticatedUser`);
    return data;
  },
  requestResetPassword: async (email) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/reset_password`, {
      email,
    });
    return data;
  },
  resetPassword: async ({ password, confirm_password, resetKey }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/reset_password/${resetKey}`, {
      password,
      confirm_password,
    });

    return data;
  },
  requestChangeTierLevel: async (form) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/change_tier_request`, form);
    return data;
  },
  requestSessionUrl: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/users/kyc_veriff`);
    return data;
  },
  createWallet: async ({ wallets }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user/addresses/create`, {
      wallets,
    });
    return data;
  },
  checkWaitList: async (email) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/account/waitlist/rank?email=${encodeURIComponent(email)}`
    );

    return data;
  },
  lifetimeTransaction: async (userId) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/account/${userId}/lifetime_transactions`
    );

    return data;
  },
  whitelistAuthEmail: async ({ token: recaptchaToken, wallets, reason }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/user/addresses/white_list/auth_email`,
      { recaptchaToken, reason, wallets }
    );

    return data;
  },
  whitelistAddresses: async ({ walletAddresses, emailAuthCode, authCode }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user/addresses/white_list/`, {
      walletAddresses,
      emailAuthCode,
      authCode,
    });

    return data;
  },
  getWhitelistAddresses: async ({ coin_id }) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/user/addresses/white_list/coin/${coin_id}`
    );

    return data;
  },
  getLastUsedWhitelistAddress: async ({ coin_id }) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/user/addresses/white_list/coin/${coin_id}/last_used`
    );

    return data;
  },
  deleteWhitelistAddress: async ({ walletsIds, emailAuthCode, authCode }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/user/addresses/white_list/remove`,
      {
        walletsIds,
        emailAuthCode,
        authCode,
      }
    );

    return data;
  },
  cardInfo: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/user/card`);

    return data;
  },
  oauthInfo: async () => {
    const { data } = await axiosConnection.get(
      `/oauth2/info?client_id=${process.env.REACT_APP_OAUTH_CLIENT_ID}`
    );
    return data;
  },
  oauthRevoke: async () => {
    const { data } = await axiosConnection.post('/oauth2/disconnect', {
      client_id: process.env.REACT_APP_OAUTH_CLIENT_ID,
    });
    return data;
  },
};

// TODO: REMOVE THIS ONE (IT IS ALREADY REMOVED FROM BE)
export const oxygenYield = {
  offers: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/oxygen_yield/products/active`);
    return data;
  },
  deposited: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/oxygen_yield/contracts/deposited`);
    return data;
  },
  received: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/oxygen_yield/contracts/received`);
    return data;
  },
  ticker: async ({ source, destination }) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/oxygen_yield/market/spot/ticker/${source}/${destination}`
    );
    return data;
  },
  offerDetails: async ({ productId, amount }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/oxygen_yield/contracts/upsert`, {
      productId,
      amount,
    });
    return data;
  },
  activateOffer: async ({ contractId }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/oxygen_yield/contracts/${contractId}/activate`
    );
    return data;
  },
  depositDetails: async ({ contractId }) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/oxygen_yield/contracts/${contractId}`
    );
    return data;
  },
};

export const otc = {
  trade: async ({
    source_currency,
    destination_currency,
    source_amount,
    destination_amount,
    trade_type,
    contact_method,
  }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/otc_trade_enquiries`, {
      source_currency,
      destination_currency,
      source_amount,
      destination_amount,
      trade_type,
      contact_method,
    });
    return data;
  },
};

export const card = {
  galileo: {
    order: async ({ currency, platform, isVirtual = false }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/order`, {
        is_virtual: isVirtual,
        platform,
        currency,
      });
      return data;
    },
    usCardImage: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/usa-card/image`);
      return data;
    },
    balance: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/balance`);
      return data;
    },
    viewPin: async ({ password }) => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/usa-card/PIN`, {
        headers: { password },
      });
      return data;
    },
    status: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/status`);
      if (data.cardStatus || data.card_status) {
        data.cardStatus = data.cardStatus ? data.cardStatus : data.card_status;
      }
      return data;
    },
    block: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/block`);
      return data;
    },
    unblock: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/unblock`);
      return data;
    },
    transactions: async ({ page = 1, size = 10, startDate, endDate }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/transactions`, {
        pageNumber: page,
        pageSize: size,
        startDate,
        endDate,
      });
      return data;
    },
    lastTransactions: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/usa-card/transactions/latest`);
      return data;
    },
    statement: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/usa-card/statement?from_date=${fromDate}&to_date=${toDate}`,
        {},
        {
          responseType: 'arraybuffer',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );
      return data;
    },
    statementNew: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/usa-card/statement-v2?from_date=${fromDate}&to_date=${toDate}`,
        {},
        {
          responseType: 'arraybuffer',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );
      return data;
    },
    sendStatementEmail: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/usa-card/statement/send_by_email?from_date=${fromDate}&to_date=${toDate}`
      );

      return data;
    },
    cardReportStolen: async ({ order_replacement, use_expedited_shipping }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/report_as_stolen`, {
        order_replacement,
        use_expedited_shipping,
      });

      return data;
    },
    cardReportLost: async ({ order_replacement, use_expedited_shipping }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/report_as_lost`, {
        order_replacement,
        use_expedited_shipping,
      });

      return data;
    },

    cardReportDamaged: async ({ use_expedited_shipping }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/report_as_damaged`, {
        use_expedited_shipping,
      });

      return data;
    },
    cardOrderReplacement: async ({ use_expedited_shipping }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/usa-card/order_replacement`, {
        use_expedited_shipping,
      });

      return data;
    },
    cardEnable: async () => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/usa-card/card_present_transactions/enable`
      );

      return data;
    },
    cardPinChangeKey: async ({ password }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/usa-card/request-pin-change`,
        {},
        {
          headers: { password },
        }
      );
      return data;
    },
  },
  visa: {
    status: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/status`);
      return data;
    },
    balance: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/balance`);
      return data;
    },
    activate: async ({ cvv }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/activate`, {
        cvv,
      });
      return data;
    },
    order: async ({ platform, currency }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/order`, {
        platform,
        currency,
      });
      return data;
    },
    transactions: async ({ row = 0, size = 10 }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/transactions`, {
        rowIndex: row,
        pageSize: size,
      });
      return data;
    },
    viewPin: async ({ cvv }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/viewPIN`, { cvv });
      return data;
    },
    block: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/block`);
      return data;
    },
    unblock: async () => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/unblock`);
      return data;
    },
    statement: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/card/statement?from_date=${fromDate}&to_date=${toDate}`,
        {},
        {
          responseType: 'arraybuffer',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );
      return data;
    },
    sendStatementEmail: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/card/statement/send_by_email?from_date=${fromDate}&to_date=${toDate}`
      );

      return data;
    },
  },
  updateDisplayCurrency: async ({ coin }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/account/card_display_currency`, {
      card_display_currency: coin,
    });
    return data;
  },
  monavate: {
    status: async () => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/card/monavate/status`);
      return data;
    },
    balance: async () => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/card/monavate/balance`);
      return data;
    },
    spendingLimits: async (cardId) => {
      const { data } = await axiosConnection.get(
        `${IFRAME_API}/v2/cards/${cardId}/spending_limits`
      );
      return data;
    },
    order: async ({ platform, isVirtual = false, currency }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/order`, {
        platform,
        isVirtual,
        currency,
      });
      return data;
    },
    activate: async ({ cvv }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/activate`, {
        cvv,
      });
      return data;
    },
    block: async () => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/block`);
      return data;
    },
    unblock: async () => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/unblock`);
      return data;
    },
    viewDetails: async ({ password }) => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/card/monavate/details`, {
        headers: { password },
      });
      return data;
    },
    setPIN: async ({ pin, existingPin }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/monavate/setPIN`, {
        existingPin,
        pin,
      });
      return data;
    },
    viewPin: async ({ password }) => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/card/monavate/viewPIN`, {
        headers: { password },
      });
      return data;
    },
    checkPIN: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/card/monavate/checkPIN`);
      return data;
    },
    statement: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/monavate/statement?from_date=${fromDate}&to_date=${toDate}`,
        {
          responseType: 'arraybuffer',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );
      return data;
    },
    statementCSV: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/monavate/statement/csv?from_date=${fromDate}&to_date=${toDate}`,
        {
          responseType: 'arraybuffer',
          headers: { 'Content-Type': 'text/csv' },
        }
      );
      return data;
    },
    sendStatementEmail: async ({ fromDate, toDate }) => {
      const { data } = await axiosConnection.post(
        `${BAANX_API}/v1/card/monavate/statement/send_by_email?from_date=${fromDate}&to_date=${toDate}`
      );

      return data;
    },
    transactions: async ({ page = 0, size = 10, searchQuery, from_date, to_date, type }) => {
      let queryKeys = `pageNumber=${page}&pageSize=${size}&includeDeclined=1`;

      if (searchQuery) {
        queryKeys += `&search=${searchQuery}`;
      }

      if (type) {
        queryKeys += `&type=${type}`;
      }

      if (from_date || to_date) {
        const fromDateFormated = from_date ? from_date.format('YYYY-MM-DD') : '';
        const toDateFormated = to_date ? to_date.format('YYYY-MM-DD') : '';
        queryKeys += `&from_date=${fromDateFormated}&to_date=${toDateFormated}`;
      }

      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/monavate/transactions?${queryKeys}`
      );
      return data;
    },
    cardReportStolen: async ({
      platform,
      isVirtual = false,
      currency,
      isFiatPayment = false,
      cardProvider,
    }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/report/stolen`, {
        platform,
        isVirtual,
        currency,
        isFiatPayment,
        cardProvider,
      });

      return data;
    },
    cardReportLost: async ({
      platform,
      isVirtual = false,
      isPremium = false,
      currency,
      isFiatPayment = false,
      cardProvider,
    }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/monavate/report/lost`, {
        platform,
        isVirtual,
        currency,
        isPremium,
        isFiatPayment,
        cardProvider,
      });

      return data;
    },
  },
  promo: {
    order: async ({ cardProvider, platform, pin }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/order/promo`, {
        cardProvider,
        platform,
        pin,
      });
      return data;
    },
  },
  waiting: {
    order: async ({ cardProvider, platform, pin }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/order/waitlist`, {
        cardProvider,
        platform,
        pin,
      });
      return data;
    },
  },
  fundingSources: async (queryKey) => {
    const { transactionId } = queryKey[1];
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/card/transaction/${transactionId}/funding_sources`
    );
    return data;
  },
  connectFinancial: {
    order: async ({ isVirtual = true } = {}) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/connect-financial/order`, {
        isVirtual,
        isMetal: false,
      });
      return data;
    },
    orderStatus: async () => {
      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/connect-financial/order/status`
      );
      return data;
    },
    status: async () => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/card/connect-financial/status`);
      return data;
    },
    block: async () => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/connect-financial/block`);
      return data;
    },
    unblock: async () => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/card/connect-financial/unblock`);
      return data;
    },
    transactions: async ({ page = 1, size = 10, startDate, endDate } = {}) => {
      const params = new URLSearchParams({ pageNumber: page, pageSize: size, includeDeclined: 1 });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/connect-financial/transactions?${params}`
      );
      return data;
    },
    statement: async ({ fromDate, toDate } = {}) => {
      if (!fromDate || !toDate) {
        throw new Error('Connect Financial statement requires fromDate and toDate');
      }
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });

      const { data } = await axiosConnection.get(
        `${BAANX_API}/v1/card/connect-financial/statement?${params}`,
        { responseType: 'arraybuffer' }
      );
      return data;
    },
    detailsPage: async () => {
      const gatewayUrl = process.env.REACT_APP_API_GATEWAY_URL;
      const clientKey = process.env.REACT_APP_API_GATEWAY_CLIENT_KEY;
      const hostedUiUrl = process.env.REACT_APP_HOSTED_UI_URL;
      if (!gatewayUrl) {
        throw new Error('REACT_APP_API_GATEWAY_URL is required to show card details');
      }
      if (!clientKey) {
        throw new Error('REACT_APP_API_GATEWAY_CLIENT_KEY is required to show card details');
      }
      if (!hostedUiUrl) {
        throw new Error('REACT_APP_HOSTED_UI_URL is required to show card details');
      }
      const sessionToken = axiosConnection?.defaults.headers.common.authorization;
      if (!sessionToken) {
        throw new Error('A signed-in session is required to show card details');
      }

      let response;
      try {
        response = await axios.post(
          `${gatewayUrl}/v1/card/details/token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${sessionToken}`,
              'x-client-key': clientKey,
              'x-us-env': String(isUsApi),
            },
            timeout: CARD_DETAILS_PAGE_TIMEOUT_MS,
          }
        );
      } catch (error) {
        if (!error?.response) {
          throw error;
        }
        const gatewayError = new Error(error.response.data?.message || 'Card details failed');
        gatewayError.status = error.response.status;
        throw gatewayError;
      }
      const { hostedPageUrl } = response.data;
      const hostedUiOrigin = new URL(hostedUiUrl).origin;
      if (typeof hostedPageUrl !== 'string' || !hostedPageUrl.startsWith(`${hostedUiOrigin}/`)) {
        throw new Error('Card details page is not served from REACT_APP_HOSTED_UI_URL');
      }
      return { hostedPageUrl };
    },
  },
  payment: {
    orderCard: async ({ isPremium, currency, cardDesignCode, cardProvider, isVirtual }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/payment/st/order/card`, {
        isPremium,
        currency,
        cardDesignCode,
        cardProvider,
        isVirtual,
      });
      return data;
    },
    buyCrypto: async ({ amount, currency, displayCurrency }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v1/payment/st/buy/crypto/${currency}`,
        {
          amount,
          currency: displayCurrency,
        }
      );
      return data;
    },

    orderableCardTypes: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/orderable_card_types`);

      return data;
    },
  },
  cryptodraft: {
    create: async ({ amount, coin }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/cryptodrafts`, {
        collateral_amount: amount,
        collateral_currency: coin,
      });
      return data;
    },
    details: async () => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/cryptodrafts/`);
      return data;
    },
    interestPayment: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/cryptodrafts/interests/due`);
      return data;
    },
    payInterestPayment: async ({ coin }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/cryptodrafts/interests/pay`, {
        source_wallet_currency: coin,
      });
      return data;
    },
    getFullSettlementInfo: async () => {
      const { data } = await axiosConnection.get(
        `${IFRAME_API}/v2/cryptodrafts/principal/full_settlement`
      );
      return data;
    },
    payFullSettlement: async ({ coin }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/cryptodrafts/principal/full_settlement`,
        {
          source_wallet_currency: coin,
        }
      );
      return data;
    },
    deposit: async ({ amount }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/cryptodrafts/collateral/deposit`,
        {
          credit_amount: amount,
        }
      );
      return data;
    },
    payPartial: async ({ coin, amount }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/cryptodrafts/principal/partial_settlement`,
        {
          source_wallet_currency: coin,
          settlement_amount: amount,
        }
      );
      return data;
    },
    getWithdrawableAmount: async (forPrincipal = null) => {
      let url = `${IFRAME_API}/v2/cryptodrafts/collateral/withdrawable`;
      if (forPrincipal !== null) {
        url += `?principal=${forPrincipal}`;
      }

      const { data } = await axiosConnection.get(url);
      return data;
    },
    requestCollateralWithdrawal: async ({ withdrawal_amount }) => {
      const { data } = await axiosConnection.post(
        `${IFRAME_API}/v2/cryptodrafts/collateral/request_withdrawal`,
        { withdrawal_amount }
      );
      return data;
    },
  },
  wallets: {
    linkedWallets: async () => {
      const { data } = await axiosConnection.get(`${IFRAME_API}/v2/card/linked_wallets`);
      return data;
    },
    updateLinkedWallets: async ({ walletsIds }) => {
      const { data } = await axiosConnection.post(`${IFRAME_API}/v2/card/linked_wallets`, {
        addressIds: walletsIds,
      });
      return data;
    },
    linkableWallets: async () => {
      const { data } = await axiosConnection.get(
        `${IFRAME_API}/v2/card/linked_wallets/available_wallets`
      );
      return data;
    },
  },
  // TODO: REMOVE THIS ONE NOT USED ANYMORE
  sca: {
    getNotifications: async () => {
      const { data } = await axiosConnection.get(`${BAANX_API}/v1/sca_notification`);
      return data;
    },
    verifyOtp: async ({ otp_code, sca_notification_id }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/sca_notification/verify_otp`, {
        otp_code,
        sca_notification_id,
      });
      return data;
    },
    resendOtp: async ({ sca_notification_id }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/sca_notification/resend_otp`, {
        sca_notification_id,
      });
      return data;
    },
    dismissNotification: async ({ sca_notification_id }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/sca_notification/dismiss`, {
        sca_notification_id,
      });
      return data;
    },
  },
};

export const accounts = {
  transactions: async ({ address, coin, page, searchQuery, from_date, to_date }) => {
    // Encoding the address solves the issue of some cryptos having characters such as ?,
    // which may be perceived as query parameters by the endpoint
    const encodedAddress = encodeURIComponent(address);

    const lowerCasedCoin = coin?.toLowerCase();

    let queryKeys = `page=${page}`;

    if (searchQuery) {
      queryKeys += `&search=${searchQuery}`;
    }

    if (from_date || to_date) {
      const fromDateFormated = from_date ? from_date.format('YYYY-MM-DD') : '';
      const toDateFormated = to_date ? to_date.format('YYYY-MM-DD') : '';
      queryKeys += `&from_date=${fromDateFormated}&to_date=${toDateFormated}`;
    }

    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/user/wallet/address/${encodedAddress}/${lowerCasedCoin}/transactions/paginated?${queryKeys}`
    );
    return data;
  },
  getMailingAddresses: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/account/mailing_addresses`);
    return data;
  },

  editMailingAddress: async ({ id, addressLine1, addressLine2, state, city, postCode }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/mailing_address/${id}`, {
      addressLine1,
      addressLine2,
      state,
      city,
      postCode,
    });
    return data;
  },
  mexc: {
    sellEstimation: async ({ source, destination, baseAmount, quoteAmount }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/exchange/sell/estimation`, {
        baseCurrencyCode: source,
        quoteCurrencyCode: destination,
        targetBaseQuantity: baseAmount,
        targetQuoteQuantity: quoteAmount,
      });
      return data;
    },
    buyEstimation: async ({ source, destination, baseAmount, quoteAmount }) => {
      const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/exchange/buy/estimation`, {
        baseCurrencyCode: source,
        quoteCurrencyCode: destination,
        targetBaseQuantity: baseAmount,
        targetQuoteQuantity: quoteAmount,
      });
      return data;
    },
  },
};

export const transactions = {
  calculateFees: async ({ source, destination, mode, amount, coin, currencyId }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/user/transactions/calculate_fee`,
      {
        source,
        destination,
        amount,
        mode,
        coin,
        supported_currencies_id: currencyId,
      }
    );
    return data;
  },

  // TODO: REMOVE IT NOT USED ANYWHERE
  calculateGenericFees: async ({ source, destination, amount, coin, speed }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v2/user/transactions/calculate_fee/generic`,
      {
        source,
        destination,
        amount,
        coin,
        speed,
      }
    );
    return data;
  },

  send: async ({ amount, source, recipient, otp, totp, coin, currencyId, memo }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user/transactions/send`, {
      amount,
      coin,
      source,
      recipient,
      otp,
      totp,
      supported_currencies_id: currencyId,
      memo,
    });

    return data;
  },
  fund: async ({ transactionId, inAmount, currency, inAddress }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/user/transactions/ledger`, {
      transactionId,
      inAmount,
      currency,
      inAddress,
    });

    return data;
  },
  bankReference: async ({ currency }) => {
    const { data } = await axiosConnection.get(
      `${BAANX_API}/v1/account/payment-reference/${currency}`
    );
    return data;
  },
  buyFiat: async ({ amount, currency, coin }) => {
    const { data } = await axiosConnection.post(
      `${IFRAME_API}/v1/payment/st/buy/fiat/${currency}`,
      {
        amount,
        currency: coin,
      }
    );
    return data;
  },
};

export const bank = {
  withdraw: async ({
    amount,
    recipientIban,
    recipientBic,
    recipientBank,
    recipientAccountNumber,
    recipientSortCode,
    recipientBankCountry,
    walletAddress,
    otpauth,
    coin,
  }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/bank_withdrawal/requests`, {
      amount,
      recipientIban,
      recipientBic,
      recipientBank,
      recipientAccountNumber,
      recipientSortCode,
      recipientBankCountry,
      walletAddress,
      otpauth,
      coin,
    });
    return data;
  },
};

export const accountSettings = {
  updateSelectedCurrency: async ({ currency }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/account/update_currency`, {
      currency,
    });
    return data;
  },
  updateRewardCashbackCurrency: async ({ cashback_currency }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/account/rewards/cashback`, {
      cashback_currency,
    });
    return data;
  },
  getRewardCashbackCurrency: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/account/rewards/cashback/coin`);
    return data;
  },
  sendCashbackRewardToUser: async () => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/account/cashback_rewards/send`);
    return data;
  },
};

export const exchange = {
  buy: async ({ source, recipient, fiat_amount, crypto_amount, toCoin }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/exchange/buy`, {
      source,
      recipient,
      fiat_amount,
      crypto_amount,
      coin: toCoin,
    });
    return data;
  },
  sell: async ({ source, recipient, fiat_amount, crypto_amount, coin }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/exchange/sell`, {
      source,
      recipient,
      fiat_amount,
      crypto_amount,
      coin,
    });
    return data;
  },
  convert: async ({ sourceCoin, destinationCoin, sourceAmount }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/user/exchange/convert`, {
      baseSymbol: sourceCoin,
      quoteSymbol: destinationCoin,
      baseAmount: sourceAmount,
    });
    return data;
  },
};

export const loans = {
  all: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/loans`);
    return data;
  },
  loanDetails: async (id) => {
    const { data } = await axiosConnection.get(`${BAANX_API}/v1/loans/${id}`);
    return data;
  },
  request: async ({
    LTV,
    interestApr,
    collateralWalletAddress,
    principalCurrency,
    collateralAmount,
    collateralCurrency,
    principalAmount,
    loanLengthDays,
  }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/loans`, {
      LTV,
      interestApr,
      collateralWalletAddress,
      principalCurrency,
      collateralAmount,
      collateralCurrency,
      principalAmount,
      loanLengthDays,
    });
    return data;
  },
  repayLoanFiat: async ({ id, amount }) => {
    const { data } = await axiosConnection.post(`${BAANX_API}/v1/loans/${id}/loan_repayment/fiat`, {
      amount,
    });
    return data;
  },
  payCollateralCrypto: async ({ loanId, amount }) => {
    const { data } = await axiosConnection.post(
      `${BAANX_API}/v1/loans/${loanId}/collateral_payment/crypto`,
      {
        amount,
      }
    );
    return data;
  },
  buyCollateral: async ({ source, recipient, fiat_amount, crypto_amount, from_coin, loanId }) => {
    const { data } = await axiosConnection.post(
      `${BAANX_API}/v1/loans/${loanId}/collateral_payment/exchange`,
      {
        source,
        recipient,
        fiat_amount,
        crypto_amount,
        from_coin,
      }
    );
    return data;
  },
  payInterestOverdue: async ({ loanId, sourceWalletCurrency }) => {
    const { data } = await axiosConnection.post(
      `${BAANX_API}/v1/loans/${loanId}/interest_payment`,
      {
        sourceWalletCurrency,
      }
    );
    return data;
  },
};

export const transfers = {
  fundingBankDetails: async ({ currency }) => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/transfers/funding_bank_details/${currency}`,
      {
        responseType: 'arraybuffer',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );
    return data;
  },
};

export const staking = {
  all: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/staking_deals`);
    return data;
  },
  details: async (id) => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/staking_deals/${id}`);
    return data;
  },
  create: async ({ amount, durationInMonths, currency }) => {
    const { data } = await axiosConnection.post(`${IFRAME_API}/v2/staking_deals`, {
      amount,
      currency,
      durationInMonths,
    });
    return data;
  },
  stakingTiers: async () => {
    const { data } = await axiosConnection.get(`${IFRAME_API}/v2/bxx_staking_tiers`);
    return data;
  },
  activeStakingAmountWithTier: async () => {
    const { data } = await axiosConnection.get(
      `${IFRAME_API}/v2/bxx_staking_tiers/user/active_staked_amount_with_tier`
    );
    return data;
  },
};
